/**
 * Dummy data for Progress DataCards (screenshot / write-in-progress mode).
 * Used when real API data is not available.
 */

import type {
	BarChartData,
	CircularChartData,
	CircularGaugeGridData,
	CircularProgressListData,
	ComparisonChartData,
	LineChartData,
	RadarChartData,
} from "@memorang/ui";

const randomHighScore = (min = 78, max = 96): number =>
	Math.floor(Math.random() * (max - min + 1)) + min;
const randomTargetMinutes = (min = 43, max = 54): number =>
	Math.floor(Math.random() * (max - min + 1)) + min;

const confidenceLabel = (pct: number): string =>
	pct >= 88 ? "High confidence" : "Good confidence";

const weeks = ["W1", "W2", "W3", "W4", "W5"] as const;

const buildRisingSeries = ({
	startMin,
	startMax,
	stepMin,
	stepMax,
}: {
	startMin: number;
	startMax: number;
	stepMin: number;
	stepMax: number;
}) => {
	let current =
		Math.floor(Math.random() * (startMax - startMin + 1)) + startMin;
	return weeks.map((week, index) => {
		if (index > 0) {
			const step =
				Math.floor(Math.random() * (stepMax - stepMin + 1)) + stepMin;
			current = Math.min(100, current + step);
		}
		return { x: week, y: current };
	});
};

const userSeries = buildRisingSeries({
	startMin: 44,
	startMax: 50,
	stepMin: 3,
	stepMax: 5,
});
const peerSeriesRaw = buildRisingSeries({
	startMin: 48,
	startMax: 55,
	stepMin: 2,
	stepMax: 4,
});
const peerSeries = peerSeriesRaw.map((point, index) => {
	const minGap = 3;
	const userPoint = userSeries[index]?.y ?? point.y;
	return {
		x: point.x,
		y: Math.max(point.y, Math.min(95, userPoint + minGap)),
	};
});

const getRandomInt = (min: number, max: number): number =>
	Math.floor(Math.random() * (max - min + 1)) + min;

const buildDummyStreakData = () => {
	const weekData: CircularChartData["weekData"] = [
		{ day: "Mon", type: "completed" },
		{ day: "Tue", type: Math.random() > 0.45 ? "completed" : "missed" },
		{ day: "Wed", type: "completed" },
		{ day: "Thu", type: "completed" },
		{ day: "Fri", type: "completed", isToday: true },
		{ day: "Sat", type: "unattempted" },
		{ day: "Sun", type: "unattempted" },
	];

	const todayIndex = weekData.findIndex((d) => d.isToday);
	let currentStreak = 0;
	for (let i = todayIndex; i >= 0; i--) {
		if (weekData[i]?.type === "completed") {
			currentStreak++;
		} else {
			break;
		}
	}

	const bestStreak = Math.max(currentStreak + getRandomInt(2, 8), 7);
	return { weekData, currentStreak, bestStreak };
};

export const dummyComparisonChartData: ComparisonChartData = {
	series1: userSeries,
	series2: peerSeries,
	xAxisLabel: "Week",
	yAxisLabel: "Performance (%)",
};

export const dummyLineChartData: LineChartData = {
	actual: [
		{ x: "W1", y: 40 },
		{ x: "W2", y: 55 },
		{ x: "W3", y: 52 },
		{ x: "W4", y: 68 },
		{ x: "W5", y: 72 },
	],
	prediction: [
		{ x: "W5", y: 72 },
		{ x: "W6", y: 78 },
		{ x: "W7", y: 82 },
	],
	targetValue: 80,
	xAxisLabel: "Week",
	yAxisLabel: "Readiness (%)",
};

export const dummyBarChartData: BarChartData = [
	{
		category: "Reading",
		minutes: randomTargetMinutes(45, 58),
		status: "quick",
	},
	{
		category: "Writing",
		minutes: randomTargetMinutes(42, 55),
		status: "average",
	},
	{
		category: "Listening",
		minutes: randomTargetMinutes(44, 57),
		status: "average",
	},
	{
		category: "Speaking",
		minutes: randomTargetMinutes(40, 53),
		status: "slow",
	},
];

export const dummyRadarChartData: RadarChartData = [
	{
		skill: "Reading",
		value: randomHighScore(),
		target: 95,
		peerAvg: randomHighScore(76, 88),
	},
	{
		skill: "Writing",
		value: randomHighScore(),
		target: 95,
		peerAvg: randomHighScore(76, 88),
	},
	{
		skill: "Listening",
		value: randomHighScore(),
		target: 95,
		peerAvg: randomHighScore(76, 88),
	},
	{
		skill: "Speaking",
		value: randomHighScore(),
		target: 95,
		peerAvg: randomHighScore(76, 88),
	},
];

const streakData = buildDummyStreakData();
export const dummyCircularChartData: CircularChartData = {
	currentStreak: streakData.currentStreak,
	bestStreak: streakData.bestStreak,
	weekData: streakData.weekData,
};

export const dummyCircularProgressListData: CircularProgressListData = [
	{
		label: "Cardiovascular",
		progress: randomHighScore(),
		count: "24 items",
		color: "#66bb68",
	},
	{
		label: "Pharmacology",
		progress: randomHighScore(),
		count: "18 items",
		color: "#9ccc65",
	},
	{
		label: "Anatomy",
		progress: randomHighScore(),
		count: "32 items",
		color: "#66bb68",
	},
	{
		label: "Pathology",
		progress: randomHighScore(),
		count: "12 items",
		color: "#9ccc65",
	},
];

export const dummyCircularGaugeGridData: CircularGaugeGridData = [
	(() => {
		const percentage = randomHighScore();
		return {
			label: "Reading",
			percentage,
			fraction: confidenceLabel(percentage),
			color: "#66bb68",
		};
	})(),
	(() => {
		const percentage = randomHighScore();
		return {
			label: "Writing",
			percentage,
			fraction: confidenceLabel(percentage),
			color: "#9ccc65",
		};
	})(),
	(() => {
		const percentage = randomHighScore();
		return {
			label: "Listening",
			percentage,
			fraction: confidenceLabel(percentage),
			color: "#66bb68",
		};
	})(),
	(() => {
		const percentage = randomHighScore();
		return {
			label: "Speaking",
			percentage,
			fraction: confidenceLabel(percentage),
			color: "#9ccc65",
		};
	})(),
];
